import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { PremiumLock } from '../components/PremiumGate';
import { usePremium } from '../hooks/usePremium';
import {
  Upload, FileText, Trash2, CheckCircle, AlertTriangle, Eye, EyeOff,
  Calendar, Building2, ChevronDown, X, Camera, Image, Loader, Shield, Lock,
  ChevronRight, Beaker, Brain, Stethoscope, Pill, Moon, FileSearch, Folder
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { value: 'blood_test', label: 'Kan tahlili', icon: Beaker },
  { value: 'imaging', label: 'Görüntüleme raporu', icon: FileSearch },
  { value: 'doctor_note', label: 'Doktor notu / Epikriz', icon: Stethoscope },
  { value: 'psychology', label: 'Psikoloji / Psikiyatri', icon: Brain },
  { value: 'medication', label: 'İlaç listesi', icon: Pill },
  { value: 'pathology', label: 'Patoloji raporu', icon: FileText },
  { value: 'sleep_report', label: 'Uyku raporu', icon: Moon },
  { value: 'other', label: 'Diğer', icon: Folder },
];

const GlassCard = ({ children, className = '', style = {}, ...props }) => (
  <div className={`relative overflow-hidden ${className}`} style={{
    background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)', borderRadius: '20px',
    border: '1px solid rgba(255,255,255,0.05)',
    boxShadow: '0 10px 40px rgba(0,0,0,0.4), inset 0 0 20px rgba(255,255,255,0.05)',
    padding: '24px', ...style,
  }} {...props}>
    {children}
    <div className="absolute bottom-0 left-0 right-0 h-[1px]" style={{
      background: 'linear-gradient(90deg, transparent, rgba(34,211,238,0.4), transparent)',
    }} />
  </div>
);

const DocumentsPage = () => {
  const { user } = useAuth();
  const { isPremium } = usePremium();  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showTechnical, setShowTechnical] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [docType, setDocType] = useState('blood_test');
  const [docDate, setDocDate] = useState('');
  const [institution, setInstitution] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const loadDocuments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/documents`, { withCredentials: true });
      setDocuments(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!consentGiven) { alert('Lütfen sağlık verisi işleme iznini onaylayın.'); return; }

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'jpg', 'jpeg', 'png'].includes(ext)) {
      alert('Desteklenmeyen format. PDF, JPG, JPEG veya PNG yükleyin.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('Dosya boyutu 10MB\'i asiyor.');
      return;
    }

    setUploading(true);
    setUploadProgress('Dosya yükleniyor...');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);
    formData.append('doc_date', docDate);
    formData.append('institution', institution);

    try {
      setUploadProgress('AI belgeyi analiz ediyor...');
      const { data } = await axios.post(`${BACKEND_URL}/api/documents/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setDocuments(prev => [data, ...prev]);
      setSelectedDoc(data);
      setShowUpload(false);
      setDocDate('');
      setInstitution('');
      setUploadProgress('');
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Yükleme sırasında hata oluştu.');
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  const handleVerify = async (docId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/documents/${docId}/verify`, {}, { withCredentials: true });
      setDocuments(prev => prev.map(d => d.document_id === docId ? { ...d, user_verified: true } : d));
      if (selectedDoc?.document_id === docId) setSelectedDoc(prev => ({ ...prev, user_verified: true }));
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Bu belgeyi silmek istediginize emin misiniz?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/documents/${docId}`, { withCredentials: true });
      setDocuments(prev => prev.filter(d => d.document_id !== docId));
      if (selectedDoc?.document_id === docId) setSelectedDoc(null);
    } catch (e) { console.error(e); }
  };

  const getTypeIcon = (type) => {
    const found = DOC_TYPES.find(d => d.value === type);
    return found?.icon || FileText;
  };

  return (
    <div className="min-h-screen relative" style={{ background: 'transparent' }}>
      {/* Stars */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 15% 30%, rgba(167,139,250,0.09) 0%, transparent 55%), radial-gradient(ellipse at 80% 20%, rgba(59,130,246,0.09) 0%, transparent 50%), radial-gradient(ellipse at 50% 85%, rgba(34,211,238,0.06) 0%, transparent 45%)',
        }} />
      </div>

      <Header />

      <main className="relative z-10 max-w-5xl mx-auto px-4 pb-16 pt-8">
        {/* Title */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.15)', boxShadow: '0 0 20px rgba(34,211,238,0.15)' }}>
              <span className="icon-lit icon-lit-cyan"><FileText className="w-6 h-6 text-cyan-400 icon-glow-cyan" /></span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white text-glow" data-testid="documents-page-title">Sağlık Belgelerim</h1>
              <p className="text-white/40 text-sm mt-1">{documents.length} belge yüklendi</p>
            </div>
          </div>
          {isPremium ? (
          <button onClick={() => { setShowUpload(true); setSelectedDoc(null); }}
            className="flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-semibold text-white transition-all hover:scale-[1.03]"
            style={{ background: 'linear-gradient(135deg, #3B82F6, #22D3EE)', boxShadow: '0 8px 25px rgba(59,130,246,0.4)' }}
            data-testid="upload-document-button">
            <Upload className="w-5 h-5" /> Belge Yükle
          </button>
          ) : (
          <button disabled
            className="flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-semibold transition-all opacity-50 cursor-not-allowed"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#94A3B8' }}
            data-testid="upload-document-button-locked">
            <Lock className="w-4 h-4" /> Belge Analizi Premium
          </button>
          )}
        </div>

        {/* Disclaimer */}
        <div className="mb-6 flex items-start gap-3 p-4 rounded-2xl" style={{
          background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)',
        }} data-testid="documents-disclaimer">
          <Shield className="w-5 h-5 text-amber-400 icon-glow-amber shrink-0 mt-0.5" />
          <p className="text-white/50 text-sm leading-relaxed">Bu sistem tıbbi tanı koymaz. Yüklediğiniz sağlık belgeleri, size daha bütüncül değerlendirme sunabilmek için kullanılacaktır. Son değerlendirme için doktorunuza başvurun.</p>
        </div>

        {/* ═══════ UPLOAD PANEL ═══════ */}
        {showUpload && (
          <GlassCard className="mb-8 animate-fade-up" data-testid="upload-panel">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-white text-glow">Yeni Belge Yükle</h2>
              <button onClick={() => setShowUpload(false)} className="text-white/30 hover:text-white/60 transition"><X className="w-5 h-5" /></button>
            </div>

            {/* Doc type selector */}
            <div className="mb-5">
              <label className="text-white/60 text-sm mb-2 block">Belge Türü</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {DOC_TYPES.map(dt => {
                  const Icon = dt.icon;
                  const active = docType === dt.value;
                  return (
                    <button key={dt.value} onClick={() => setDocType(dt.value)}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium transition-all"
                      style={{
                        background: active ? 'rgba(34,211,238,0.12)' : 'rgba(255,255,255,0.04)',
                        border: active ? '1px solid rgba(34,211,238,0.4)' : '1px solid rgba(255,255,255,0.06)',
                        color: active ? '#22D3EE' : 'rgba(255,255,255,0.5)',
                        boxShadow: active ? '0 0 15px rgba(34,211,238,0.12)' : 'none',
                      }} data-testid={`doc-type-${dt.value}`}>
                      <Icon className="w-4 h-4" /> {dt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Optional fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              <div>
                <label className="text-white/60 text-sm mb-2 block flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Belge Tarihi (opsiyonel)</label>
                <input type="date" value={docDate} onChange={e => setDocDate(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-cyan-400/50" data-testid="doc-date-input" />
              </div>
              <div>
                <label className="text-white/60 text-sm mb-2 block flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Kurum Adi (opsiyonel)</label>
                <input type="text" value={institution} onChange={e => setInstitution(e.target.value)} placeholder="Ör: Istanbul Tip Fakültesi"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-cyan-400/50 placeholder:text-white/20" data-testid="doc-institution-input" />
              </div>
            </div>

            {/* Consent */}
            <label className="flex items-start gap-3 mb-5 cursor-pointer p-3 rounded-xl transition-all" style={{
              background: consentGiven ? 'rgba(52,211,153,0.06)' : 'rgba(255,255,255,0.02)',
              border: consentGiven ? '1px solid rgba(52,211,153,0.15)' : '1px solid rgba(255,255,255,0.05)',
            }}>
              <input type="checkbox" checked={consentGiven} onChange={e => setConsentGiven(e.target.checked)}
                className="mt-1 w-4 h-4 rounded text-cyan-400" data-testid="consent-checkbox" />
              <span className="text-white/50 text-sm leading-relaxed">
                Sağlık belgelerimin AI tarafından analiz edilmesini ve daha bütüncül değerlendirme için kullanılmasını onaylıyorum. Bu veriler sadece bana özel olarak saklanacaktır.
              </span>
            </label>

            {/* Upload buttons */}
            {uploading ? (
              <div className="flex items-center justify-center gap-3 py-8">
                <Loader className="w-6 h-6 text-cyan-400 animate-spin icon-glow-cyan" />
                <span className="text-cyan-300 text-sm font-medium">{uploadProgress}</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                  onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0])} data-testid="file-input" />
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0])} data-testid="camera-input" />

                <button onClick={() => fileInputRef.current?.click()} disabled={!consentGiven}
                  className="flex flex-col items-center gap-2 py-6 rounded-2xl transition-all hover:scale-[1.02] disabled:opacity-30"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                  data-testid="select-file-button">
                  <FileText className="w-8 h-8 text-cyan-400 icon-glow-cyan" />
                  <span className="text-white/70 text-sm font-medium">Dosya Sec</span>
                  <span className="text-white/30 text-xs">PDF, JPG, PNG</span>
                </button>

                <button onClick={() => { const i = document.createElement('input'); i.type='file'; i.accept='image/*'; i.onchange=e=>e.target.files?.[0]&&handleUpload(e.target.files[0]); i.click(); }}
                  disabled={!consentGiven}
                  className="flex flex-col items-center gap-2 py-6 rounded-2xl transition-all hover:scale-[1.02] disabled:opacity-30"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                  data-testid="photo-upload-button">
                  <Image className="w-8 h-8 text-purple-400 icon-glow-purple" />
                  <span className="text-white/70 text-sm font-medium">Fotograf Yükle</span>
                  <span className="text-white/30 text-xs">Galeri</span>
                </button>

                <button onClick={() => cameraInputRef.current?.click()} disabled={!consentGiven}
                  className="flex flex-col items-center gap-2 py-6 rounded-2xl transition-all hover:scale-[1.02] disabled:opacity-30"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                  data-testid="camera-button">
                  <Camera className="w-8 h-8 text-amber-400 icon-glow-amber" />
                  <span className="text-white/70 text-sm font-medium">Kamera</span>
                  <span className="text-white/30 text-xs">Canli cekim</span>
                </button>
              </div>
            )}
          </GlassCard>
        )}

        {/* ═══════ SELECTED DOC DETAIL ═══════ */}
        {selectedDoc && (
          <GlassCard className="mb-8 animate-fade-up" data-testid="document-detail-card">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                {(() => { const Icon = getTypeIcon(selectedDoc.doc_type); return <Icon className="w-5 h-5 text-cyan-400 icon-glow-cyan" />; })()}
                <div>
                  <h3 className="text-white font-bold text-glow">{selectedDoc.file_name}</h3>
                  <p className="text-white/40 text-xs mt-0.5">{selectedDoc.doc_type_label} • {selectedDoc.doc_date || 'Tarih belirtilmemiş'} {selectedDoc.institution ? `• ${selectedDoc.institution}` : ''}</p>
                </div>
              </div>
              <button onClick={() => setSelectedDoc(null)} className="text-white/30 hover:text-white/60"><X className="w-5 h-5" /></button>
            </div>

            {/* AI Summary — Plain */}
            <div className="mb-5 p-4 rounded-2xl" style={{ background: 'rgba(34,211,238,0.06)', border: '1px solid rgba(34,211,238,0.12)' }}>
              <h4 className="text-cyan-400 text-sm font-semibold mb-2 flex items-center gap-2">
                <Brain className="w-4 h-4 icon-glow-cyan" /> AI Değerlendirmesi
              </h4>
              <p className="text-white/70 text-sm leading-relaxed" data-testid="ai-plain-summary">
                {selectedDoc.ai_analysis?.plain_summary || 'Analiz yapılıyor...'}
              </p>
            </div>

            {/* Attention points */}
            {selectedDoc.ai_analysis?.attention_points?.length > 0 && (
              <div className="mb-5 p-4 rounded-2xl" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.12)' }}>
                <h4 className="text-amber-400 text-sm font-semibold mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 icon-glow-amber" /> Dikkat Ceken Noktalar
                </h4>
                <ul className="space-y-1.5">
                  {selectedDoc.ai_analysis.attention_points.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/60 text-sm">
                      <span className="text-amber-400 mt-1">•</span> {p}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Extracted data table */}
            {selectedDoc.extracted_data?.length > 0 && (
              <div className="mb-5">
                <h4 className="text-white/60 text-sm font-semibold mb-3 flex items-center gap-2">
                  <Beaker className="w-4 h-4" /> Cikarilan Veriler
                  <span className="text-white/30 text-xs">(AI tarafından algilandi, lütfen kontrol edin)</span>
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-white/40 text-xs">
                        <th className="text-left py-2 px-3">Test / Bulgu</th>
                        <th className="text-left py-2 px-3">Deger</th>
                        <th className="text-left py-2 px-3">Referans</th>
                        <th className="text-left py-2 px-3">Birim</th>
                        <th className="text-left py-2 px-3">Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedDoc.extracted_data.map((f, i) => (
                        <tr key={i} className="border-t border-white/5">
                          <td className="py-2.5 px-3 text-white/80 font-medium">{f.name}</td>
                          <td className="py-2.5 px-3 text-white/70">{f.value}</td>
                          <td className="py-2.5 px-3 text-white/40">{f.reference_range}</td>
                          <td className="py-2.5 px-3 text-white/40">{f.unit}</td>
                          <td className="py-2.5 px-3">
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                              background: f.flag === 'normal' ? 'rgba(52,211,153,0.15)' : f.flag === 'yüksek' ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
                              color: f.flag === 'normal' ? '#34D399' : f.flag === 'yüksek' ? '#EF4444' : '#FBBF24',
                            }}>{f.flag || '-'}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Technical toggle */}
            {selectedDoc.ai_analysis?.technical_summary && (
              <div className="mb-5">
                <button onClick={() => setShowTechnical(!showTechnical)}
                  className="flex items-center gap-2 text-white/40 text-sm hover:text-white/60 transition" data-testid="toggle-technical">
                  {showTechnical ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showTechnical ? 'Teknik Görünümü Kapat' : 'Detayli Teknik Görünüm'}
                </button>
                {showTechnical && (
                  <div className="mt-3 p-4 rounded-2xl animate-fade-up" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <p className="text-white/50 text-sm leading-relaxed whitespace-pre-line">{selectedDoc.ai_analysis.technical_summary}</p>
                  </div>
                )}
              </div>
            )}

            {/* Verify / actions */}
            <div className="flex items-center gap-3 pt-4 border-t border-white/5">
              {!selectedDoc.user_verified ? (
                <>
                  <button onClick={() => handleVerify(selectedDoc.document_id)}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:scale-[1.03]"
                    style={{ background: 'linear-gradient(135deg, #34D399, #22D3EE)', boxShadow: '0 6px 20px rgba(52,211,153,0.3)' }}
                    data-testid="verify-document-button">
                    <CheckCircle className="w-4 h-4" /> Onayliyorum
                  </button>
                  <span className="text-white/30 text-xs">AI tarafindan cikarilan verilerin dogrulugunu onaylayin</span>
                </>
              ) : (
                <div className="flex items-center gap-2 text-emerald-400 text-sm">
                  <CheckCircle className="w-4 h-4 icon-glow-green" /> Dogrulanmis belge
                </div>
              )}
              <div className="flex-1" />
              <button onClick={() => handleDelete(selectedDoc.document_id)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm text-red-400/60 hover:text-red-400 hover:bg-red-500/10 transition"
                data-testid="delete-document-button">
                <Trash2 className="w-4 h-4" /> Sil
              </button>
            </div>
          </GlassCard>
        )}

        {/* ═══════ DOCUMENT LIST ═══════ */}
        <GlassCard data-testid="documents-list-card">
          <h2 className="text-lg font-bold text-white text-glow mb-5 flex items-center gap-3">
            <Folder className="w-5 h-5 text-purple-400 icon-glow-purple" /> Yüklenen Belgeler
          </h2>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-6 h-6 text-cyan-400 animate-spin" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-white/10 mx-auto mb-4" />
              <p className="text-white/30 text-sm">Henüz belge yüklenmemis.</p>
              <button onClick={() => setShowUpload(true)}
                className="mt-4 text-cyan-400 text-sm font-medium hover:text-cyan-300 transition" data-testid="upload-first-doc">
                Ilk belgenizi yükleyin
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {documents.map(doc => {
                const Icon = getTypeIcon(doc.doc_type);
                return (
                  <button key={doc.document_id} onClick={() => { setSelectedDoc(doc); setShowTechnical(false); }}
                    className="w-full flex items-center gap-4 p-4 rounded-xl transition-all glass-card-hover text-left relative overflow-hidden"
                    style={{
                      background: selectedDoc?.document_id === doc.document_id ? 'rgba(34,211,238,0.06)' : 'rgba(255,255,255,0.02)',
                      border: selectedDoc?.document_id === doc.document_id ? '1px solid rgba(34,211,238,0.2)' : '1px solid rgba(255,255,255,0.04)',
                    }} data-testid={`doc-row-${doc.document_id}`}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(34,211,238,0.1)' }}>
                      <Icon className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white/90 text-sm font-medium truncate">{doc.file_name}</p>
                        {doc.user_verified && <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                      </div>
                      <p className="text-white/40 text-xs mt-0.5 truncate">
                        {doc.doc_type_label} • {doc.doc_date || new Date(doc.created_at).toLocaleDateString('tr-TR')}
                        {doc.ai_analysis?.plain_summary ? ` • ${doc.ai_analysis.plain_summary.slice(0, 60)}...` : ''}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/20 shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </GlassCard>
      </main>

      <Footer />
    </div>
  );
};

export default DocumentsPage;
